import React, { useState, useEffect } from "react";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { generatePdfFromElement } from "@/utils/pdfGenerator";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface PdfSettings {
  company_name?: string;
  header_text?: string;
  footer_text?: string;
  header_image_url?: string;
  font_family?: string;
  font_size_body?: number;
  font_size_footnote?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  line_spacing?: number;
  paragraph_indent?: number;
  page_numbering_enabled?: boolean;
  page_numbering_position?: string;
  lawyer_name?: string;
  oab_number?: string;
  oab_state?: string;
  include_lawyer_signature?: boolean;
}

interface PdfDownloadButtonProps {
  content: string;
  messageId: string;
}

export const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({
  content,
  messageId
}) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfSettings, setPdfSettings] = useState<PdfSettings | null>(null);

  useEffect(() => {
    if (user) {
      loadPdfSettings();
    }
  }, [user]);

  const loadPdfSettings = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações de PDF:', error);
        return;
      }
      
      setPdfSettings(data);
    } catch (error) {
      console.error('Erro ao carregar configurações de PDF:', error);
    }
  };

  const extractTextFromHtml = (htmlContent: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    textContent = textContent.replace(/\n\s*\n/g, '\n\n');
    return textContent.trim();
  };

  const buildHtmlWithLetterhead = (documentContent: string): string => {
    const settings = pdfSettings || {};
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Resposta Justiçai Legal</title>
        <style>
          body { 
            font-family: '${settings.font_family || 'Times New Roman'}', serif; 
            font-size: ${settings.font_size_body || 12}pt;
            line-height: ${settings.line_spacing || 1.5};
            margin: 0;
            padding: 30mm 20mm 20mm 30mm;
            color: black;
            box-sizing: border-box;
            width: 100%;
            overflow-x: hidden;
          }
          .header {
            text-align: center;
            margin-bottom: 2em;
            padding-bottom: 1em;
            border-bottom: 1px solid #ccc;
          }
          .header img {
            max-height: 80px;
            margin-bottom: 1em;
          }
          .company-name {
            font-size: 18pt;
            font-weight: bold;
            margin-bottom: 0.5em;
          }
          .header-text {
            font-size: ${settings.font_size_footnote || 10}pt;
            color: #666;
          }
          p { 
            text-align: justify; 
            margin-bottom: 1em;
            text-indent: 0;
            margin-left: 0;
            margin-right: 0;
            word-wrap: break-word;
            overflow-wrap: break-word;
            hyphens: auto;
          }
          .content > p {
            text-indent: ${settings.paragraph_indent || 0}mm;
          }
          .footer {
            margin-top: 4em;
            text-align: center;
            font-size: ${settings.font_size_footnote || 10}pt;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 1em;
          }
          .signature-area { 
            margin-top: 4em; 
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #000;
            width: 300px;
            margin: 0 auto 1em;
            padding-top: 0.5em;
          }
        </style>
      </head>
      <body>
        ${settings.company_name || settings.header_text || settings.header_image_url ? `
          <div class="header">
            ${settings.header_image_url ? `<img src="${settings.header_image_url}" alt="Logo">` : ''}
            ${settings.company_name ? `<div class="company-name">${settings.company_name}</div>` : ''}
            ${settings.header_text ? `<div class="header-text">${settings.header_text}</div>` : ''}
          </div>
        ` : ''}
        
        <div class="content">
          ${extractTextFromHtml(documentContent).split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')}
        </div>

        ${settings.include_lawyer_signature && (settings.lawyer_name || settings.oab_number) ? `
          <div class="signature-area">
            <div class="signature-line"></div>
            ${settings.lawyer_name ? `<div>${settings.lawyer_name}</div>` : ''}
            ${settings.oab_number || settings.oab_state ? `<div>OAB/${settings.oab_state || 'XX'} ${settings.oab_number || 'XXXXXX'}</div>` : ''}
          </div>
        ` : `
          <div class="signature-area">
            <div class="signature-line"></div>
            <div>Assinatura</div>
            <div>Data: ${new Date().toLocaleDateString('pt-BR')}</div>
          </div>
        `}

        ${settings.footer_text ? `
          <div class="footer">
            ${settings.footer_text}
          </div>
        ` : ''}
      </body>
      </html>
    `;
  };

  const oldCreateFormattedContent = (content: string): string => {
    // CORREÇÃO: Extrair apenas texto limpo sem tags HTML para PDF
    const formatContent = (text: string) => {
      // Extrair texto limpo se houver HTML
      let cleanText = text;
      if (text.includes('<') && text.includes('>')) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        cleanText = tempDiv.innerText || tempDiv.textContent || '';
      }
      
      // Processar apenas formatação básica para PDF
      return cleanText
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.trim() ? `<p>${line}</p>` : '')
        .join('');
    };

    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Documento - Justiçai Legal</title>
        <style>
          @page {
            size: A4;
            margin: 2.5cm 3cm 2cm 3cm;
            @bottom-center {
              content: counter(page);
              font-size: 10pt;
              color: #666;
            }
          }
          
          body {
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.5;
            color: #000;
            margin: 0;
            padding: 0;
            background: white;
            text-align: justify;
            text-justify: inter-word;
          }
          
          h1, h2, h3 {
            font-weight: bold;
            margin-top: 18pt;
            margin-bottom: 12pt;
            text-align: center;
          }
          
          h1 { font-size: 14pt; }
          h2 { font-size: 13pt; }
          h3 { font-size: 12pt; }
          
          p {
            margin: 6pt 0;
            text-indent: 1.25cm;
          }
          
          .artigo {
            text-indent: 0;
            margin-top: 12pt;
            margin-bottom: 6pt;
          }
          
          .paragrafo {
            text-indent: 1.25cm;
            margin: 6pt 0;
          }
          
          .inciso {
            text-indent: 2.5cm;
            margin: 3pt 0;
          }
          
          .alinea {
            text-indent: 3.75cm;
            margin: 3pt 0;
          }
          
          ul, ol {
            margin: 6pt 0;
            padding-left: 1.25cm;
          }
          
          li {
            margin: 3pt 0;
            text-indent: 0;
          }
          
          strong {
            font-weight: bold;
          }
          
          em {
            font-style: italic;
          }
          
          code {
            font-family: 'Courier New', monospace;
            background: #f5f5f5;
            padding: 2px 4px;
            border-radius: 2px;
          }
        </style>
      </head>
      <body>
        <div id="content">
          ${formatContent(content)}
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPdf = async () => {
    setIsGenerating(true);
    
    try {
      // Criar iframe invisível e isolado (SOLUÇÃO DEFINITIVA contra vazamento de CSS)
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `
        position: fixed !important;
        left: -99999px !important;
        top: -99999px !important;
        width: 900px !important;
        height: 1123px !important;
        border: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
        opacity: 0 !important;
      `;
      
      document.body.appendChild(iframe);
      
      // Escrever HTML no contexto isolado do iframe
      const iframeDoc = iframe.contentDocument;
      if (!iframeDoc) throw new Error('Não foi possível acessar documento do iframe');

      iframeDoc.open();
      iframeDoc.write(buildHtmlWithLetterhead(content));
      iframeDoc.close();

      // Aguardar renderização completa
      await new Promise<void>(resolve => {
        if (iframeDoc.readyState === 'complete') {
          resolve();
        } else {
          iframe.onload = () => resolve();
        }
      });
      await new Promise(resolve => setTimeout(resolve, 50));

      // Elemento para captura (body do iframe - contexto isolado)
      const tempDiv = iframeDoc.body;
      
      // Gerar PDF
      await generatePdfFromElement(tempDiv, {
        filename: `resposta-ia-${messageId}-${Date.now()}.pdf`,
        margin: 15,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          logging: false
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true
        }
      });
      
      // Remover iframe temporário
      document.body.removeChild(iframe);
      
      toast({
        title: "PDF baixado com sucesso!",
        description: "O documento foi salvo em sua pasta de downloads.",
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro ao gerar PDF",
        description: "Não foi possível baixar o documento. Tente novamente.",
        variant: "destructive",
        duration: 4000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-5 w-5 md:h-6 md:w-6" 
          onClick={handleDownloadPdf}
          disabled={isGenerating}
          aria-label="Baixar como PDF"
        >
          <FileDown className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{isGenerating ? "Gerando PDF..." : "Baixar como PDF"}</p>
      </TooltipContent>
    </Tooltip>
  );
};